import {
  Link,
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  retainSearchParams,
  useMatches,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { AppShell } from './components/AppShell.tsx';
import { EidReturnPage } from './components/Eid.tsx';
import type { RecordTable } from './lib/api.ts';
import { useEntities } from './lib/api.ts';
import { useT } from './lib/preferences.tsx';
import { LANDING, belongsTo } from './lib/profile.ts';
import type { EntityScope } from './lib/scope.ts';
import { defaultScope, parseScope, scopeOnCompanyPage, serializeScope } from './lib/scope.ts';
import { useTenant } from './lib/tenant.tsx';

// Screens load on demand; the shell, switcher and drawer are in the initial bundle.
const StructurePage = lazyRouteComponent(() => import('./pages/Structure.tsx'), 'StructurePage');
const EntityPage = lazyRouteComponent(() => import('./pages/Entity.tsx'), 'EntityPage');
const LedgerPage = lazyRouteComponent(() => import('./pages/Ledger.tsx'), 'LedgerPage');
const BeliefsPage = lazyRouteComponent(() => import('./pages/Beliefs.tsx'), 'BeliefsPage');
const ChatPage = lazyRouteComponent(() => import('./pages/Chat.tsx'), 'ChatPage');
const ApprovalsPage = lazyRouteComponent(() => import('./pages/Approvals.tsx'), 'ApprovalsPage');
const DocumentsPage = lazyRouteComponent(() => import('./pages/Documents.tsx'), 'DocumentsPage');
const AccountsPage = lazyRouteComponent(() => import('./pages/Accounts.tsx'), 'AccountsPage');
const BankPage = lazyRouteComponent(() => import('./pages/Bank.tsx'), 'BankPage');
const AttentionPage = lazyRouteComponent(() => import('./pages/Attention.tsx'), 'AttentionPage');
const RelationshipsPage = lazyRouteComponent(
  () => import('./pages/Relationships.tsx'),
  'RelationshipsPage',
);
// Who a person is and what they own and owe, by hand (Personal step 3).
const ProfilePage = lazyRouteComponent(() => import('./pages/Profile.tsx'), 'ProfilePage');
const FinancesPage = lazyRouteComponent(() => import('./pages/Finances.tsx'), 'FinancesPage');
// The Record (Personal step 2): one module, three screens, so the three load together.
const ConversationsPage = lazyRouteComponent(
  () => import('./pages/Record.tsx'),
  'ConversationsPage',
);
const CalendarPage = lazyRouteComponent(() => import('./pages/Record.tsx'), 'CalendarPage');
const FilesPage = lazyRouteComponent(() => import('./pages/Record.tsx'), 'FilesPage');
const ConnectCallbackPage = lazyRouteComponent(
  () => import('./pages/ConnectCallback.tsx'),
  'ConnectCallbackPage',
);
const ConnectAgentPage = lazyRouteComponent(
  () => import('./pages/ConnectAgent.tsx'),
  'ConnectAgentPage',
);
const SignInWithAuthoPage = lazyRouteComponent(
  () => import('./pages/SignInWithAutho.tsx'),
  'SignInWithAuthoPage',
);
const ConnectTodoistCallbackPage = lazyRouteComponent(
  () => import('./pages/ConnectTodoistCallback.tsx'),
  'ConnectTodoistCallbackPage',
);
const ConnectBankCallbackPage = lazyRouteComponent(
  () => import('./pages/ConnectBankCallback.tsx'),
  'ConnectBankCallbackPage',
);
const CounterpartiesPage = lazyRouteComponent(
  () => import('./pages/Counterparties.tsx'),
  'CounterpartiesPage',
);
const ClarifyPage = lazyRouteComponent(() => import('./pages/Clarify.tsx'), 'ClarifyPage');
const AuditPage = lazyRouteComponent(() => import('./pages/Audit.tsx'), 'AuditPage');
const ConfigurePage = lazyRouteComponent(() => import('./pages/Configure.tsx'), 'ConfigurePage');
const SettingsPage = lazyRouteComponent(() => import('./pages/Settings.tsx'), 'SettingsPage');
const AgentsPage = lazyRouteComponent(() => import('./pages/Agents.tsx'), 'AgentsPage');

export interface RootSearch {
  /** Entity scope: `one.<id>` or `sel.<id>,<id>` (an Org's screens). `combined` is retired and lands on the default. */
  scope?: string | undefined;
  /** Open provenance drawer: `<table>:<id>`. */
  provenance?: string | undefined;
}

/**
 * **A screen of the other kind of profile is not opened, it is left** (R4). A link to Relationships followed while a
 * company is open, or to Bank while the person's own profile is, goes to the open profile's landing page instead of
 * drawing a screen that profile has nothing for. The scope goes too: it named the other profile's companies, if it
 * named anything. The same table decides the menu and the command palette (`lib/profile.ts`), so a screen this lets
 * through is one the menu shows.
 */
function ProfileGuard({ children }: { children: ReactNode }) {
  const { kind } = useTenant();
  /**
   * **Judged by the screen about to be drawn, not by the address.** While a navigation is under way the address is
   * already the new one and the matched screen still the old: a guard that read the address let the old screen
   * draw once more after its redirect began — an entity page fetching an id this profile has never held. The
   * deepest match is what the outlet below renders, so it is what is judged.
   */
  const drawn = useMatches({ select: (matches) => matches.at(-1)?.pathname ?? '/' });
  // One element per kind, so a redirect that is under way is not asked for again on every render while it lands.
  const away = useMemo(
    () => <Navigate to={LANDING[kind]} search={{ scope: undefined }} replace />,
    [kind],
  );
  if (!belongsTo(kind, drawn)) return away;
  return children;
}

/**
 * An address that is no screen — `/group` among them since R4 — says so in words, inside the shell, with the way back
 * to where the open profile starts, rather than the router's own untranslated *Not Found*.
 */
function NotFound() {
  const t = useT();
  const { kind } = useTenant();
  return (
    <div className="px-6 py-4" data-testid="not-found">
      <h1 className="text-title">{t('notFound.title')}</h1>
      <p className="mt-1 text-sm text-muted">{t('notFound.body')}</p>
      <p className="mt-3 text-sm">
        <Link
          to={LANDING[kind]}
          search={{ scope: undefined }}
          className="text-accent-text hover:underline focus-visible:outline-2 focus-visible:outline-accent"
        >
          {t('notFound.home', { screen: t(kind === 'me' ? 'nav.attention' : 'nav.structure') })}
        </Link>
      </p>
    </div>
  );
}

const rootRoute = createRootRoute({
  validateSearch: (search: Record<string, unknown>): RootSearch => ({
    scope: typeof search.scope === 'string' ? search.scope : undefined,
    provenance: typeof search.provenance === 'string' ? search.provenance : undefined,
  }),
  search: { middlewares: [retainSearchParams(['scope'])] },
  component: () => (
    <AppShell>
      <ProfileGuard>
        <Outlet />
      </ProfileGuard>
    </AppShell>
  ),
  notFoundComponent: NotFound,
});

/**
 * Where a person lands: a company's profile on its structure, a person's own on what needs them (Batch E.2) — whose
 * empty state is written for both cases, nothing connected yet and nothing needing anybody, so a first run still
 * reads as an invitation to connect an account rather than as a blank page.
 */
function Landing() {
  const { kind } = useTenant();
  return <Navigate to={LANDING[kind]} search={(prev) => prev} replace />;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Landing,
});
const structureRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/structure',
  component: StructurePage,
});

/**
 * **A company's own page is the company in view** (F356). Its address names the company twice — in the path, and in
 * `?scope=` — and the page drew the path while the sidebar's switcher and the banner read the scope, so a page could
 * show one company under a switcher naming another. Whichever way the page is opened — Structure's table, the
 * palette, a link kept from another day — the scope is made that company, before anything is drawn, by replacing the
 * address rather than adding to the history.
 */
function companyInView(to: '/structure/$entityId' | '/structure/$entityId/ledger') {
  return ({ params, search }: { params: { entityId: string }; search: RootSearch }) => {
    const scope = scopeOnCompanyPage(params.entityId, search.scope);
    if (scope !== null) throw redirect({ to, params, search: { ...search, scope }, replace: true });
  };
}

const entityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/structure/$entityId',
  beforeLoad: companyInView('/structure/$entityId'),
  component: EntityPage,
});
const ledgerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/structure/$entityId/ledger',
  beforeLoad: companyInView('/structure/$entityId/ledger'),
  component: LedgerPage,
});
const beliefsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/beliefs',
  component: BeliefsPage,
});
/** Chat (slice 6): in both profiles, each its own threads. */
const chatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chat',
  component: ChatPage,
});
const documentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents',
  component: DocumentsPage,
});
const counterpartiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/counterparties',
  component: CounterpartiesPage,
});
const bankRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bank',
  component: BankPage,
});
const accountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accounts',
  component: AccountsPage,
});
const attentionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attention',
  component: AttentionPage,
});
const relationshipsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/relationships',
  component: RelationshipsPage,
});
// One person's card, with what they are part of (Personal step 6).
const PersonCardPage = lazyRouteComponent(() => import('./pages/PersonCard.tsx'), 'PersonCardPage');
const personCardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/relationships/$personId',
  component: PersonCardPage,
});
const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  component: ProfilePage,
});
// Goals and the person's own Todoist list, read live (Personal step 4).
const GoalsPage = lazyRouteComponent(() => import('./pages/Goals.tsx'), 'GoalsPage');
const goalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/goals',
  component: GoalsPage,
});
const financesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/finances',
  component: FinancesPage,
});
const conversationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/conversations',
  component: ConversationsPage,
});
const calendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/calendar',
  component: CalendarPage,
});
const filesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/files',
  component: FilesPage,
});
/** Where Google's redirect lands. Registered with Google, so the path is not free to change (F120). */
const connectCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connect/google/callback',
  component: ConnectCallbackPage,
});
/** The OAuth consent screen. Published in the server metadata, so the path is not free to change (O16). */
const connectAgentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connect/agent',
  component: ConnectAgentPage,
});
/**
 * Sign in with Autho's consent screen (F494, F508): the provider sends the browser here, so the path is the one
 * `apps/api/src/services/oidc.ts` names.
 */
const signInWithAuthoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in-with-autho/$uid',
  component: function SignInWithAutho() {
    const { uid } = signInWithAuthoRoute.useParams();
    return <SignInWithAuthoPage uid={uid} />;
  },
});
/** Where Todoist's redirect lands. Registered with Todoist, so the path is not free to change (D57). */
const connectTodoistRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connect/todoist/callback',
  component: ConnectTodoistCallbackPage,
});

/**
 * Where Vipps or BankID sends a person back to add the eID to the one signed in (0127). Registered with each provider,
 * so the path is not free to change; a sign-in coming back here is completed by the gate before anybody is signed in.
 */
const eidReturnRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/eid/$provider/return',
  component: function EidReturn() {
    const { provider } = eidReturnRoute.useParams();
    return provider === 'vipps' || provider === 'bankid' ? (
      <EidReturnPage provider={provider} />
    ) : null;
  },
});

const connectBankRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connect/bank/callback',
  component: ConnectBankCallbackPage,
});
const clarifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clarify',
  component: ClarifyPage,
});
const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit',
  component: AuditPage,
});
const configureRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/configure',
  component: ConfigurePage,
});
const agentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/configure/agents',
  component: AgentsPage,
});
/** Approvals (F369): the kill switch, what waits for a person, and the rules. `item` is the one a task links. */
const approvalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/approvals',
  validateSearch: (search: Record<string, unknown>): { item?: string | undefined } => ({
    item: typeof search.item === 'string' ? search.item : undefined,
  }),
  component: ApprovalsPage,
});
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  eidReturnRoute,
  indexRoute,
  structureRoute,
  entityRoute,
  ledgerRoute,
  beliefsRoute,
  chatRoute,
  documentsRoute,
  counterpartiesRoute,
  accountsRoute,
  attentionRoute,
  profileRoute,
  relationshipsRoute,
  personCardRoute,
  financesRoute,
  goalsRoute,
  conversationsRoute,
  calendarRoute,
  filesRoute,
  bankRoute,
  connectCallbackRoute,
  connectAgentRoute,
  signInWithAuthoRoute,
  connectTodoistRoute,
  connectBankRoute,
  clarifyRoute,
  auditRoute,
  configureRoute,
  agentsRoute,
  approvalsRoute,
  settingsRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * **The scope a person chose**, read from the address: null when it names none. The screens that are the profile's
 * own record rather than a view of one company — the audit log and Clarify's questions — narrow by this and by
 * nothing else, so that with nothing chosen they show the whole profile, as they did when the default was
 * *Combined* (R4).
 */
export function useChosenScope(): EntityScope | null {
  const search = useSearch({ strict: false }) as RootSearch;
  return parseScope(search.scope);
}

/**
 * **The scope in force**: the one chosen, else the Org's own company, else its first managed entity (R4: *Combined*
 * left the app). Null only while there is no entity to put in scope. Setting null goes back to the default.
 */
export function useScope(): [EntityScope | null, (scope: EntityScope | null) => void] {
  const chosen = useChosenScope();
  const { tenantId, membership } = useTenant();
  const entities = useEntities(tenantId);
  const navigate = useNavigate();
  const scope = chosen ?? defaultScope(entities.data ?? [], membership.orgnr);
  const setScope = useCallback(
    (next: EntityScope | null) => {
      void navigate({
        to: '.',
        search: (prev: RootSearch) => ({
          ...prev,
          scope: next === null ? undefined : serializeScope(next),
        }),
      });
    },
    [navigate],
  );
  return [scope, setScope];
}

export function useProvenanceParam(): [
  { table: RecordTable; id: string } | null,
  (target: { table: RecordTable; id: string } | null) => void,
] {
  const search = useSearch({ strict: false }) as RootSearch;
  const navigate = useNavigate();
  const [table, id] = (search.provenance ?? '').split(':');
  const tables: RecordTable[] = [
    'legal_entity',
    'entity_role',
    'sub_unit',
    'filed_accounts',
    'me_item',
  ];
  const current =
    table && id && tables.includes(table as RecordTable)
      ? { table: table as RecordTable, id }
      : null;
  const set = useCallback(
    (target: { table: RecordTable; id: string } | null) => {
      void navigate({
        to: '.',
        search: (prev: RootSearch) => ({
          ...prev,
          provenance: target ? `${target.table}:${target.id}` : undefined,
        }),
      });
    },
    [navigate],
  );
  return [current, set];
}
